/**
 * IssueCardsDialog — "Issue new card" for the Command Center fleet block.
 *
 * Same contract as the Tap Engine Cards page: card id(s) + location (+ org for
 * super admins) + optional destination, created through the existing
 * POST /api/cards/bulk (which audits each card server-side). The Tap Engine
 * Cards page is deliberately untouched.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { cardsAPI } from '../../lib/api';
import { sortByName } from '../../lib/sortByName';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

const PLATFORMS = [
    { value: 'google_reviews', label: 'Google Reviews' },
    { value: 'facebook', label: 'Facebook Reviews' },
    { value: 'instagram', label: 'Instagram' },
    { value: 'trustpilot', label: 'Trustpilot' },
    { value: 'yelp', label: 'Yelp' },
    { value: 'tripadvisor', label: 'Tripadvisor' },
];

export default function IssueCardsDialog({
    open, onOpenChange, locations = [], organizations = [], isSuperAdmin = false,
    defaultLocationId = '', defaultOrganizationId = '', onCreated,
}) {
    const { t } = useTranslation();
    const [cardIds, setCardIds] = useState('');
    const [locationId, setLocationId] = useState(defaultLocationId || '');
    const [orgId, setOrgId] = useState(defaultOrganizationId || '');
    const [platform, setPlatform] = useState('__default__');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setCardIds('');
        setLocationId(defaultLocationId || '');
        // Pre-selected from the Command Center's organization when there is one.
        setOrgId(defaultOrganizationId || '');
        setPlatform('__default__');
    }, [open, defaultLocationId, defaultOrganizationId]);

    const sortedLocations = useMemo(() => sortByName(locations), [locations]);
    const sortedOrgs = useMemo(() => sortByName(organizations), [organizations]);
    const visibleLocations = useMemo(
        () => (isSuperAdmin && orgId ? sortedLocations.filter((l) => l.organization_id === orgId) : sortedLocations),
        [isSuperAdmin, orgId, sortedLocations],
    );

    const submit = async () => {
        const ids = cardIds.split(/[,\n]/).map((x) => x.trim()).filter(Boolean);
        if (ids.length === 0) { toast.error(t('card_fleet.ops_actions.issue_need_ids')); return; }
        if (!locationId) { toast.error(t('card_fleet.ops_actions.issue_need_location')); return; }
        const location = visibleLocations.find((l) => l.id === locationId);
        const organization_id = isSuperAdmin ? (orgId || location?.organization_id) : location?.organization_id;
        if (!organization_id) { toast.error(t('card_fleet.ops_actions.issue_need_org')); return; }
        setSaving(true);
        try {
            await cardsAPI.createBulk({
                card_ids: ids,
                organization_id,
                location_id: locationId,
                platform: platform === '__default__' ? undefined : platform,
            });
            toast.success(t('card_fleet.ops_actions.issue_created', { count: ids.length }));
            onOpenChange(false);
            onCreated?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t('card_fleet.ops_actions.issue_failed'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="cc-fleet-issue-dialog">
                <DialogHeader>
                    <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                        {t('card_fleet.ops_actions.issue_new')}
                    </DialogTitle>
                    <DialogDescription>{t('card_fleet.ops_actions.issue_desc')}</DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    <div>
                        <Label className="mb-2 block">{t('card_fleet.ops_actions.issue_ids_label')}</Label>
                        <textarea
                            value={cardIds}
                            onChange={(e) => setCardIds(e.target.value)}
                            placeholder={'BRASPC002\nBRASPC003'}
                            rows={4}
                            className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-background text-foreground px-3 py-2 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-sky-400/40"
                            data-testid="cc-fleet-issue-ids"
                        />
                    </div>

                    {isSuperAdmin && (
                        <div>
                            <Label className="mb-2 block">{t('common.organization')}</Label>
                            <Select value={orgId} onValueChange={(v) => { setOrgId(v); setLocationId(''); }}>
                                <SelectTrigger data-testid="cc-fleet-issue-org">
                                    <SelectValue placeholder={t('common.organization')} />
                                </SelectTrigger>
                                <SelectContent>
                                    {sortedOrgs.map((o) => (
                                        <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}

                    <div>
                        <Label className="mb-2 block">{t('common.location')}</Label>
                        <Select value={locationId} onValueChange={setLocationId}>
                            <SelectTrigger data-testid="cc-fleet-issue-location">
                                <SelectValue placeholder={t('common.location')} />
                            </SelectTrigger>
                            <SelectContent>
                                {visibleLocations.map((l) => (
                                    <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <div>
                        <Label className="mb-2 block">{t('card_fleet.ops_actions.issue_destination_label')}</Label>
                        <Select value={platform} onValueChange={setPlatform}>
                            <SelectTrigger data-testid="cc-fleet-issue-platform">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__default__">{t('card_fleet.ops_actions.issue_destination_default')}</SelectItem>
                                {PLATFORMS.map((p) => (
                                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cc-fleet-issue-cancel">
                        {t('common.cancel')}
                    </Button>
                    <Button onClick={submit} disabled={saving} data-testid="cc-fleet-issue-submit">
                        {t('card_fleet.ops_actions.issue_submit')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
